<template>
    <span class="phone-copy-button">
        <button type="button"
                class="phone-copy-trigger mx-1"
                :title="tooltipText"
                aria-haspopup="menu"
                :aria-expanded="menuOpen ? 'true' : 'false'"
                @click.prevent="copyDefault"
                @contextmenu.prevent="openMenu">
            <i class="fas fa-copy" />
        </button>
        <div v-if="menuOpen"
             ref="menu"
             class="dropdown-menu show phone-copy-menu"
             role="menu"
             :style="menuStyle"
             @click.stop>
            <div class="dropdown-header">Copy as</div>
            <button v-for="opt in options"
                    :key="opt.format"
                    type="button"
                    role="menuitem"
                    class="dropdown-item"
                    @click="selectFormat(opt.format)">
                {{ opt.label }}
            </button>
        </div>
    </span>
</template>

<script lang="ts">
import { Component, Prop, Vue } from "vue-property-decorator";
import PhoneFormatter, {
    PhoneCopyFormat,
    PhoneCopyFormatOption,
} from "@/util/PhoneFormatter";
import { NotificationOptions } from "@/util/NotificationOptionsPresets";
import { copyToClipboard } from "@/util/Clipboard";

const VIEWPORT_MARGIN = 8;

@Component({ name: "PhoneCopyButton" })
export default class PhoneCopyButton extends Vue {
    @Prop({ required: true }) private phone!: string | null | undefined;
    @Prop({ default: PhoneCopyFormat.E164 }) private defaultFormat!: PhoneCopyFormat;

    private menuOpen = false;
    private menuX = 0;
    private menuY = 0;
    private readonly tooltipText = "Copy (right-click for formats)";

    get options(): PhoneCopyFormatOption[] {
        return PhoneFormatter.copyFormatOptions(this.phone);
    }

    get menuStyle(): Record<string, string> {
        return {
            left: `${this.menuX}px`,
            top: `${this.menuY}px`,
        };
    }

    copyDefault(): void {
        this.copyAs(this.defaultFormat);
    }

    openMenu(event: MouseEvent): void {
        this.menuX = event.clientX;
        this.menuY = event.clientY;
        this.menuOpen = true;
        this.$nextTick(() => {
            this.clampMenuToViewport();
            // Capture phase so dismissal still fires even when another component
            // (e.g. a BootstrapVue dropdown toggle) calls stopPropagation on the
            // event before it would bubble up to document.
            document.addEventListener("click", this.closeMenuOnOutsidePointer, true);
            document.addEventListener("contextmenu", this.closeMenuOnOutsidePointer, true);
            document.addEventListener("keydown", this.handleKeydown);
            window.addEventListener("scroll", this.closeMenu, true);
            window.addEventListener("resize", this.closeMenu);
        });
    }

    closeMenu(): void {
        if (!this.menuOpen) return;
        this.menuOpen = false;
        document.removeEventListener("click", this.closeMenuOnOutsidePointer, true);
        document.removeEventListener("contextmenu", this.closeMenuOnOutsidePointer, true);
        document.removeEventListener("keydown", this.handleKeydown);
        window.removeEventListener("scroll", this.closeMenu, true);
        window.removeEventListener("resize", this.closeMenu);
    }

    closeMenuOnOutsidePointer(event: MouseEvent): void {
        // Close on any click / right-click outside this control. Guard on the whole
        // component root — trigger and menu are siblings — so interacting with the
        // trigger or a menu item doesn't self-close, while clicking elsewhere (or
        // right-clicking a different phone button) dismisses this menu.
        if (this.$el && this.$el.contains(event.target as Node)) return;
        this.closeMenu();
    }

    handleKeydown(event: KeyboardEvent): void {
        if (event.key === "Escape") this.closeMenu();
    }

    selectFormat(format: PhoneCopyFormat): void {
        this.copyAs(format);
        this.closeMenu();
    }

    private clampMenuToViewport(): void {
        const menu = this.$refs.menu as HTMLElement | undefined;
        if (!menu) return;
        const { offsetWidth, offsetHeight } = menu;
        const maxLeft = window.innerWidth - offsetWidth - VIEWPORT_MARGIN;
        const maxTop = window.innerHeight - offsetHeight - VIEWPORT_MARGIN;
        if (this.menuX > maxLeft) this.menuX = Math.max(VIEWPORT_MARGIN, maxLeft);
        if (this.menuY > maxTop) this.menuY = Math.max(VIEWPORT_MARGIN, maxTop);
    }

    private async copyAs(format: PhoneCopyFormat): Promise<void> {
        const value = PhoneFormatter.formatAs(this.phone, format);
        if (!value) return;
        if (!(await copyToClipboard(value))) {
            this.$notification(NotificationOptions.error("Could not copy phone number to clipboard."));
        }
    }

    beforeDestroy(): void {
        this.closeMenu();
    }
}
</script>

<style scoped>
.phone-copy-button {
    display: inline-block;
}

.phone-copy-trigger {
    background: none;
    border: 0;
    padding: 0;
    color: inherit;
    cursor: pointer;
    font: inherit;
    line-height: 1;
}

.phone-copy-trigger:focus {
    outline: none;
}

.phone-copy-trigger > i {
    color: #007bff;
}

/* Reuse Bootstrap's .dropdown-menu / .dropdown-item / .dropdown-header look.
   Override only what a cursor-anchored context menu needs that a normal
   dropdown does not: fixed positioning against the viewport (menuX/menuY are
   clientX/clientY), forced visibility (we mount/unmount via v-if rather than
   toggling .show), and a z-index that clears Bootstrap modals (1050). */
.phone-copy-menu.dropdown-menu {
    position: fixed;
    display: block;
    z-index: 2000;
}

/* The theme adds a caret (.dropdown-menu:before/:after) so a menu appears to
   point at its toggle. This menu is anchored to the cursor with no toggle, so
   the caret points at nothing — remove it. */
.phone-copy-menu.dropdown-menu::before,
.phone-copy-menu.dropdown-menu::after {
    display: none;
}
</style>
